import React, { useState, useEffect } from 'react';

const SegmentShowcase = () => {
  const [currentSegment, setCurrentSegment] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const segments = [
    { 
      id: 6, 
      title: "AI Data Scientists - ML Ops, Data Strategy & Gen AI", 
      icon: "🧮", 
      color: "from-emerald-500 to-green-500", 
      experts: "165",
      profiles: [
        { name: "Dr. Sarah Chen", title: "Lead ML Engineer", experience: "12 years", expertise: "Neural Networks & MLOps" },
        { name: "Alex Kumar", title: "Data Strategy Director", experience: "15 years", expertise: "AI Transformation" },
        { name: "Maria Rodriguez", title: "Senior Data Scientist", experience: "10 years", expertise: "Generative AI" },
        { name: "David Park", title: "ML Platform Architect", experience: "14 years", expertise: "Production ML" }
      ]
    },
    { 
      id: 9, 
      title: "B2B Global Sales - Product & Services", 
      icon: "📊", 
      color: "from-blue-500 to-cyan-500", 
      experts: "156",
      profiles: [
        { name: "James Wilson", title: "VP Global Sales", experience: "16 years", expertise: "Enterprise SaaS Sales" },
        { name: "Lisa Thompson", title: "Director Channel Sales", experience: "13 years", expertise: "Partner Ecosystems" },
        { name: "Michael Brown", title: "Senior Sales Manager", experience: "11 years", expertise: "B2B Product Sales" },
        { name: "Rachel Green", title: "Strategic Sales Lead", experience: "12 years", expertise: "Global Markets" }
      ]
    },
    { 
      id: 1, 
      title: "AI Agent Builders - Marketing & Operations", 
      icon: "⚙️", 
      color: "from-purple-500 to-violet-500", 
      experts: "112",
      profiles: [
        { name: "Tom Anderson", title: "AI Solutions Architect", experience: "13 years", expertise: "Marketing Automation" },
        { name: "Sophie Miller", title: "Operations AI Lead", experience: "11 years", expertise: "Process Optimization" },
        { name: "Ryan Davis", title: "Marketing Tech Director", experience: "14 years", expertise: "AI-Driven Campaigns" },
        { name: "Emily White", title: "AI Operations Manager", experience: "10 years", expertise: "Workflow Intelligence" }
      ]
    }
  ];

  // All 15 segments for the grid display (with professional icons) - Sequential numbering starting with AI Agent Builders as #1
  const allSegments = [
    { id: 1, title: "AI Agent Builders - Marketing & Operations", icon: "⚙️", experts: "112" },
    { id: 2, title: "Enterprise Applications (JS / Open Source)", icon: "💻", experts: "134" },
    { id: 3, title: "Enterprise Applications (.Net & Others)", icon: "🔧", experts: "87" },
    { id: 4, title: "Low Code App Dev - Top 10 Platforms / Tools", icon: "🛠️", experts: "92" },
    { id: 5, title: "Cyber Security - Monitoring & Prevention", icon: "🔒", experts: "73" },
    { id: 6, title: "AI Data Scientists - ML Ops, Data Strategy & Gen AI", icon: "🧮", experts: "165" },
    { id: 7, title: "D2C Brand + Performance Marketing", icon: "📈", experts: "127" },
    { id: 8, title: "B2B Ads + Lead Generation", icon: "🎯", experts: "89" },
    { id: 9, title: "B2B Global Sales - Product & Services", icon: "📊", experts: "156" },
    { id: 10, title: "Account Manager - Growth & Delivery", icon: "📋", experts: "94" },
    { id: 11, title: "AI Driven Customer Success Ops", icon: "🤝", experts: "78" },
    { id: 12, title: "HR Ops - AI Optimized with Human in Loop Processes", icon: "👤", experts: "81" },
    { id: 13, title: "Finance Ops - AI Optimized with Human in Loop Processes", icon: "💼", experts: "96" },
    { id: 14, title: "BFSI Specialized Roles - AI + Human Ops", icon: "🏛️", experts: "107" },
    { id: 15, title: "Retail / Logistics / Procurement - AI + Human Ops", icon: "📦", experts: "118" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(false);
      setTimeout(() => {
        setCurrentSegment((prev) => (prev + 1) % segments.length);
        setIsAnimating(true);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const current = segments[currentSegment];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-20">
      <div className="absolute inset-0 opacity-20"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">
              15 Domain Expert
            </span>{' '}
            <span className="text-white">Categories</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-4xl mx-auto">
            Discover our comprehensive range of AI-powered domain experts across every industry
          </p>
        </div>

        <div className="relative">
          {/* Main Display Card */}
          <div className="flex justify-center mb-8">
            <div className={`relative w-full max-w-2xl h-64 rounded-2xl bg-gradient-to-br ${current.color} p-1 shadow-2xl transform transition-all duration-700 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-75'}`}>
              <div className="w-full h-full bg-white/10 backdrop-blur-lg rounded-xl p-8 flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-6xl mb-4">{current.icon}</div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                    {current.id}. {current.title}
                  </h3>
                  <div className="flex items-center space-x-6 text-white/90">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-lg font-semibold">{current.experts} Experts</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                      <span className="text-lg">Available Now</span>
                    </div>
                  </div>
                </div>
                
                {/* Floating Icons */}
                <div className="hidden md:block relative">
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
                    <span className="text-2xl">🚀</span>
                  </div>
                  <div className="absolute top-8 -right-8 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                    <span className="text-xl">⭐</span>
                  </div>
                  <div className="absolute -top-8 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center animate-ping">
                    <span className="text-lg">💡</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex justify-center mb-8">
            <div className="flex space-x-2">
              {segments.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentSegment 
                      ? 'w-12 bg-orange-400' 
                      : index < currentSegment 
                        ? 'w-6 bg-gray-400' 
                        : 'w-6 bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Grid of All Segments */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {allSegments.map((segment, index) => (
              <div
                key={segment.id}
                className={`relative p-4 rounded-xl transition-all duration-500 cursor-pointer ${
                  segments.findIndex(s => s.id === segment.id) === currentSegment
                    ? 'bg-gradient-to-br from-orange-500/20 to-pink-500/20 border-2 border-orange-400 scale-105'
                    : 'bg-white/5 hover:bg-white/10 border border-gray-700'
                }`}
                onClick={() => {
                  const segmentIndex = segments.findIndex(s => s.id === segment.id);
                  if (segmentIndex !== -1) {
                    setCurrentSegment(segmentIndex);
                  }
                }}
              >
                <div className="text-2xl mb-2">{segment.icon}</div>
                <div className="text-white text-sm font-medium leading-tight">
                  {segment.id}. {segment.title.split(' - ')[0]}
                </div>
                <div className="text-gray-400 text-xs mt-1">
                  {segment.experts} experts
                </div>
                
                {/* Active indicator */}
                {segments.findIndex(s => s.id === segment.id) === currentSegment && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-400 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <div className="inline-flex items-center space-x-4 bg-gradient-to-r from-orange-500 to-pink-500 p-6 rounded-2xl">
              <div className="text-white">
                <div className="text-2xl font-bold">Ready to Get Started?</div>
                <div className="text-orange-100">Connect with domain experts today</div>
              </div>
              <button 
                onClick={() => window.open('https://calendly.com/superhunt/45min', '_blank')}
                className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SegmentShowcase;